import type { BranchSummary, MemberCardView } from '@khaddi/contracts';
import {
  Button,
  Chip,
  colors,
  brandSurface,
  ErrorNote,
  formatKm,
  formatVnd,
  Icon,
  IconButton,
  LogoMark,
  LogoWordmark,
  ProgressBar,
  Screen,
  SectionHeader,
  Segmented,
  Spinner,
  Text,
  useApi,
  useAsync,
  useLayout,
  useRouter,
  useSession,
} from '@khaddi/ui';
import { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { ProductGrid } from '../components/ProductCard';
import { useTabNav } from '../components/TabNav';
import { CHANNEL_OPTIONS, useCart } from '../state/cart';
import { useFeatures } from '../state/appConfig';
import { useCatalog } from '../state/catalog';


const ACTIVE_STATUSES = new Set(['pending_payment', 'new', 'brewing', 'awaiting_shipper', 'shipper_assigned', 'delivering']);

export const HomeScreen = () => {
  const nav = useTabNav('home');
  const { wide } = useLayout();
  const api = useApi();
  const { user } = useSession();
  const { navigate } = useRouter();
  const cart = useCart();
  const { onlineOrdering } = useFeatures();
  const { catalog, loading, error, reload, branch } = useCatalog();
  const [categoryId, setCategoryId] = useState<string | null>(null);

  const me = useAsync(() => (user ? api.auth.me() : Promise.resolve(null)), [api, user?.id]);
  const orders = useAsync(() => (user ? api.orders.mine() : Promise.resolve([])), [api, user?.id]);
  const hasActiveOrder = (orders.data ?? []).some((o) => ACTIVE_STATUSES.has(o.status));

  const drinkCategories = useMemo(() => (catalog ? catalog.categories.filter((c) => catalog.drinks.some((d) => d.categoryId === c.id)) : []), [catalog]);
  const category = drinkCategories.find((c) => c.id === categoryId) ?? null;
  const visibleDrinks = (catalog?.drinks ?? []).filter((d) => d.visibleOnApp);
  const featured = category ? visibleDrinks.filter((d) => d.categoryId === category.id) : visibleDrinks.filter((d) => d.bestSeller);
  const others = category ? [] : visibleDrinks.filter((d) => !d.bestSeller);

  const actions = (
    <View style={{ flexDirection: 'row', gap: 8 }}>
      <View>
        <IconButton
          icon="bell"
          label={hasActiveOrder ? 'Thông báo: có đơn đang xử lý' : 'Thông báo đơn hàng'}
          background="rgba(255,255,255,.35)"
          onPress={() => navigate('/orders')}
        />
        {hasActiveOrder ? (
          <View pointerEvents="none" style={{ position: 'absolute', top: 9, right: 10, width: 9, height: 9, borderRadius: 5, backgroundColor: colors.red, borderWidth: 2, borderColor: colors.sky }} />
        ) : null}
      </View>
      {onlineOrdering ? <IconButton icon="bag" label={`Giỏ hàng, ${cart.count} món`} badge={cart.count || null} onPress={() => navigate('/checkout')} /> : null}
    </View>
  );
  const greeting = (
    <View>
      <Text>{user ? `Chào ${user.name.split(' ').pop() ?? 'bạn'},` : 'Chào bạn,'}</Text>
      <Text variant="display">Hôm nay uống gì nè?</Text>
    </View>
  );
  const channelPicker = (
    cart.channels.length > 1 ? (
      <Segmented label="Hình thức nhận món" options={CHANNEL_OPTIONS} value={cart.channel} onChange={cart.setChannel} />
    ) : onlineOrdering ? (
      <Text variant="caption" color={colors.text}>
        {cart.channel === 'app_pickup' ? 'Đang ở quán? Order ngay trên app và nhận món tại quầy.' : 'Đặt món trên app, quán giao tận nơi.'}
      </Text>
    ) : (
      <Text variant="caption" color={colors.text}>
        Order tại quầy và đọc số điện thoại cho thu ngân để tích điểm.
      </Text>
    )
  );
  const memberBlock = (
    user ? (
      me.loading ? (
        <View style={{ ...brandSurface, borderRadius: 22, height: 150, justifyContent: 'center' }}>
          <Spinner color={colors.text} />
        </View>
      ) : me.data?.member ? (
        <View style={{ gap: 10 }}>
          <MemberCard member={me.data.member} onOpen={() => navigate('/rewards')} />
          {!me.data.user.phoneVerified || !me.data.user.hasPin ? (
            <AccountNudge verified={me.data.user.phoneVerified} onOpen={() => navigate('/verify')} />
          ) : null}
        </View>
      ) : me.error ? (
        <ErrorNote message={me.error} onRetry={() => void me.reload()} />
      ) : null
    ) : (
      <GuestCard onLogin={() => navigate('/login?next=%2F')} />
    )
  );

  return (
    <Screen nav={nav}>
      {wide ? (
        <View style={{ backgroundColor: colors.sky, margin: 16, marginBottom: 0, borderRadius: 24, padding: 24, flexDirection: 'row', alignItems: 'flex-start', gap: 24 }}>
          <View style={{ flex: 1, gap: 16, paddingTop: 8 }}>
            {greeting}
            <View style={{ maxWidth: 420 }}>{channelPicker}</View>
          </View>
          <View style={{ width: 400, gap: 12 }}>
            <View style={{ alignSelf: 'flex-end' }}>{actions}</View>
            {memberBlock}
          </View>
        </View>
      ) : (
        <>
          <View style={{ backgroundColor: colors.sky, paddingTop: 20, paddingHorizontal: 20, paddingBottom: 72, gap: 16 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <LogoWordmark />
              {actions}
            </View>
            {greeting}
            {channelPicker}
          </View>
          <View style={{ marginTop: -56, marginHorizontal: 16 }}>{memberBlock}</View>
        </>
      )}

      <View style={{ paddingTop: 18, paddingHorizontal: 16, gap: 12 }}>
        {loading ? <Spinner /> : null}
        {error && !catalog ? <ErrorNote message={error} onRetry={() => void reload()} /> : null}
        {catalog && branch ? (
          <View style={wide ? { maxWidth: 480 } : null}>
            <BranchPicker branches={catalog.branches} selected={branch} distanceKm={cart.channel === 'app_delivery' && cart.delivery.address ? cart.delivery.distanceKm : null} onSelect={cart.setBranchId} />
          </View>
        ) : null}

        {catalog ? (
          <>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }} style={{ marginHorizontal: -16 }}>
              <View style={{ width: 8 }} />
              <Chip label="Tất cả" tone="solid" selected={!category} onPress={() => setCategoryId(null)} />
              {drinkCategories.map((c) => (
                <Chip key={c.id} label={c.name} tone="solid" selected={category?.id === c.id} onPress={() => setCategoryId(c.id)} />
              ))}
              <View style={{ width: 8 }} />
            </ScrollView>

            <SectionHeader title={category ? category.name : 'Bán chạy tuần này'} action={{ label: 'Xem tất cả', onPress: () => navigate('/menu') }} />
            {featured.length ? <ProductGrid products={featured} /> : <Text variant="caption">Chưa có món nào trong nhóm này.</Text>}

            {others.length ? (
              <>
                <SectionHeader title="Thêm lựa chọn" />
                <ProductGrid products={others} />
              </>
            ) : null}
          </>
        ) : null}
      </View>
    </Screen>
  );
};

const MemberCard = ({ member, onOpen }: { member: MemberCardView; onOpen: () => void }) => (
  <View style={{ ...brandSurface, borderRadius: 22, padding: 18, gap: 12, overflow: 'hidden' }}>
    <View pointerEvents="none" style={{ position: 'absolute', right: -36, top: -30 }}>
      <LogoMark size={170} opacity={0.45} />
    </View>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <View>
        <View style={{ alignSelf: 'flex-start', backgroundColor: colors.gold, paddingHorizontal: 10, paddingVertical: 3, borderRadius: 999 }}>
          <Text variant="label" style={{ fontSize: 12, fontWeight: '700', color: colors.navy }}>
            Hạng {member.tier.name}
          </Text>
        </View>
        <Text variant="display" color={colors.text} numeric style={{ marginTop: 8, fontSize: 32, lineHeight: 34 }}>
          {member.points.toLocaleString('vi-VN')}{' '}
          <Text variant="heading" color={colors.text} style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 16, fontWeight: '600' }}>
            điểm
          </Text>
        </Text>
      </View>
      <Pressable accessibilityRole="link" accessibilityLabel="Mã thành viên" onPress={onOpen} style={{ alignItems: 'center', gap: 4 }}>
        <View style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="qr" size={26} color={colors.text} />
        </View>
        <Text variant="label" color={colors.text} style={{ fontSize: 12 }}>
          Mã thành viên
        </Text>
      </Pressable>
    </View>
    <View style={{ gap: 6 }}>
      <ProgressBar percent={member.progressPercent} color={colors.navy} track="rgba(255,255,255,.6)" />
      <Text variant="caption" color={colors.text}>
        {member.nextTier ? (
          <>
            Còn {member.pointsToNextTier.toLocaleString('vi-VN')} điểm nữa để lên{' '}
            <Text variant="caption" color={colors.text} style={{ fontWeight: '700' }}>
              Hạng {member.nextTier.name}
            </Text>
          </>
        ) : (
          'Bạn đang ở hạng cao nhất · cảm ơn đã đồng hành cùng Khao Ddi'
        )}
      </Text>
    </View>
  </View>
);

/** Unverified accounts can't redeem, and accounts from the OTP era have no PIN yet. */
const AccountNudge = ({ verified, onOpen }: { verified: boolean; onOpen: () => void }) => (
  <Pressable
    accessibilityRole="button"
    onPress={onOpen}
    style={{ backgroundColor: colors.goldSoft, borderRadius: 14, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}
  >
    <Text variant="caption" color={colors.goldText} style={{ flex: 1, fontWeight: '600' }}>
      {verified ? 'Tạo mã PIN để đăng nhập lần sau bằng số điện thoại.' : 'Xác minh số điện thoại tại quầy để đổi quà.'}
    </Text>
    <Icon name="chevronRight" size={18} color={colors.goldText} />
  </Pressable>
);

const GuestCard = ({ onLogin }: { onLogin: () => void }) => (
  <View style={{ ...brandSurface, borderRadius: 22, padding: 18, gap: 12, overflow: 'hidden' }}>
    <View pointerEvents="none" style={{ position: 'absolute', right: -36, top: -30 }}>
      <LogoMark size={170} opacity={0.45} />
    </View>
    <View style={{ gap: 4 }}>
      <Text variant="title" color={colors.text}>
        Thành viên Khao Ddi
      </Text>
      <Text variant="caption" color={colors.text}>
        Đăng ký bằng số điện thoại để tích {formatVnd(1000)} = 1 điểm và đổi quà.
      </Text>
    </View>
    <Button label="Đăng nhập / Đăng ký" size="md" variant="secondary" onPress={onLogin} style={{ alignSelf: 'flex-start', backgroundColor: colors.gold, borderColor: colors.gold }} />
  </View>
);

const BranchPicker = ({
  branches,
  selected,
  distanceKm,
  onSelect,
}: {
  branches: BranchSummary[];
  selected: BranchSummary;
  distanceKm: number | null;
  onSelect: (id: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const caption = distanceKm !== null ? `Cửa hàng phục vụ · ${formatKm(distanceKm)}` : `Cửa hàng phục vụ · ${selected.openTime}–${selected.closeTime}`;
  return (
    <View style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderRadius: 14, overflow: 'hidden' }}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={`Đổi cửa hàng, đang chọn ${selected.name}`}
        onPress={() => setOpen((o) => !o)}
        style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 12, minHeight: 44 }}
      >
        <Icon name="pin" size={20} color={colors.primaryText} />
        <View style={{ flex: 1 }}>
          <Text variant="label" style={{ fontWeight: '400' }}>
            {caption}
          </Text>
          <Text variant="body" style={{ fontWeight: '600' }}>
            {selected.name}
          </Text>
        </View>
        <View style={{ transform: [{ rotate: open ? '180deg' : '0deg' }] }}>
          <Icon name="chevronDown" size={18} color={colors.textMuted} />
        </View>
      </Pressable>
      {open
        ? branches.map((b) => {
            const usable = b.acceptsOnlineOrders && b.isOpen;
            const on = b.id === selected.id;
            return (
              <Pressable
                key={b.id}
                accessibilityRole="radio"
                accessibilityState={{ checked: on, disabled: !usable }}
                disabled={!usable}
                onPress={() => {
                  onSelect(b.id);
                  setOpen(false);
                }}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 12, minHeight: 52, borderTopWidth: 1, borderTopColor: colors.divider, backgroundColor: on ? colors.surfaceMuted : colors.surface, opacity: usable ? 1 : 0.55 }}
              >
                <View style={{ flex: 1 }}>
                  <Text variant="body" style={{ fontWeight: on ? '700' : '600' }}>
                    {b.name}
                  </Text>
                  <Text variant="label" style={{ fontWeight: '400' }}>
                    {b.address} · {usable ? `${b.openTime}–${b.closeTime}` : b.isOpen ? 'Không nhận đơn online' : 'Đang đóng cửa'}
                  </Text>
                </View>
                {on ? <Icon name="check" size={18} color={colors.primaryText} strokeWidth={2.6} /> : null}
              </Pressable>
            );
          })
        : null}
    </View>
  );
};
